import React, {useEffect, useState} from 'react';
import DialogComponent from 'components/Dialog';
import {DialogTypes} from 'common/names';
import {
   List,
   ListItem,
   IconButton,
   Grid,
   Typography,
   Checkbox,
   Box,
   FormControlLabel,
   TextField,
} from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/DeleteOutline';
import Button from 'components/Button';
import {useTranslation} from 'react-i18next';
import Panel from 'components/Panel';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {IThreatIntelligenceData} from 'types/diplomat/sftp/ThreatIntelligenceData';
import AsyncContainer from 'components/AsyncContainer';
import {usePostApi} from 'common/hooks/usePostApi';
import ConfirmationWrapper from 'components/buttons/ConfirmationWrapper';

const ThreatIntelCacheDialog = ({onClose}: {onClose: () => void}) => {
   const {t} = useTranslation();
   const [selectedIP, setSelectedIP] = useState<string | null>(null);

   const {request, state} = useGetApi<{entries: string[]}>(api.cachedhostsThreatIntel);
   useEffect(request, [request]);

   const options = {onSuccess: request};
   const {request: clearCache} = usePostApi(api.clearcacheThreatIntel, options);
   const {request: removeEntries} = usePostApi(api.removecacheentriesThreatIntel, options);

   const handleDelete = () => {
      // const confirm = window.confirm('Are you sure you want to delete the cache entry?');
      // if (confirm) {
      //    // Perform deletion here
      // }
      removeEntries(JSON.stringify({entries: [selectedIP]}));
   };

   const {
      request: getForIP,
      state: threatDetails,
      loading: threatDetailsLoadin,
   } = useGetApi<IThreatIntelligenceData>(api.getcachedhostintelThreatIntel);

   const [filterText, setFilterText] = useState('');

   // Filter the data based on the text input
   const filteredData = (state?.entries ?? []).filter((item) => {
      return item.toLowerCase().includes(filterText.toLowerCase());
   });

   return (
      <DialogComponent maxWidth="sm" header={'Threat Intelligence Cache'} close={onClose}>
         <Grid container direction="column" spacing={1}>
            <Grid item>
               <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography>
                     {t(
                        'Responses to Threat Intelligence lookups are cached for 24 hours on the Diplomat MFT Server. View and manage the cache below.'
                     )}
                  </Typography>
               </Box>
            </Grid>
            <Grid item alignSelf="end">
               <ConfirmationWrapper onOk={() => clearCache(null)}>
                  <Button>{t('Clear Cache')}</Button>
               </ConfirmationWrapper>
               <ConfirmationWrapper disabled={!selectedIP} onOk={handleDelete}>
                  <IconButton
                     disabled={!selectedIP}
                     // onClick={handleDelete}
                     sx={{verticalAlign: 'bottom', marginLeft: '1rem'}}
                  >
                     <ClearRoundedIcon />
                  </IconButton>
               </ConfirmationWrapper>
            </Grid>

            <Grid item>
               <List
                  dense
                  sx={{
                     maxHeight: '18rem',
                     minHeight: '18rem',
                     overflowY: 'scroll',
                  }}
               >
                  {filteredData.map((ip) => (
                     <ListItem
                        key={ip}
                        button
                        selected={selectedIP === ip}
                        onClick={() => {
                           setSelectedIP(ip);
                           getForIP(`?${new URLSearchParams({host: ip})}`);
                        }}
                     >
                        {ip}
                     </ListItem>
                  ))}
               </List>
            </Grid>
            <Grid item>
               <TextField
                  label="Search"
                  variant="outlined"
                  fullWidth
                  onChange={(e) => setFilterText(e.target.value)}
                  disabled={state?.entries?.length === 0}
               />
            </Grid>
            <Grid item>
               <Panel>
                  {threatDetails || threatDetailsLoadin ? (
                     <AsyncContainer loading={threatDetailsLoadin}>
                        {threatDetails && (
                           <Grid container spacing={1}>
                              <Grid item xs={6}>
                                 <FormControlLabel
                                    control={
                                       <Checkbox checked={threatDetails.isKnownAttacker} disabled />
                                    }
                                    label="Known attackers"
                                 />
                                 <FormControlLabel
                                    control={
                                       <Checkbox checked={threatDetails.isKnownSpam} disabled />
                                    }
                                    label="Known abuser"
                                 />
                                 <FormControlLabel
                                    control={<Checkbox checked={threatDetails.isTor} disabled />}
                                    label="Tor connection"
                                 />
                                 <FormControlLabel
                                    control={<Checkbox checked={threatDetails.isVPN} disabled />}
                                    label="VPN connection"
                                 />
                              </Grid>
                              <Grid item xs={6}>
                                 <FormControlLabel
                                    control={<Checkbox checked={threatDetails.isProxy} disabled />}
                                    label="Proxy connection"
                                 />
                                 <FormControlLabel
                                    control={
                                       <Checkbox checked={threatDetails.isDataCenter} disabled />
                                    }
                                    label="Data Center connection"
                                 />

                                 <Typography margin="1rem 0">
                                    Threat Score: {threatDetails.threatScore}
                                 </Typography>
                                 <Typography margin="1rem 0">
                                    Trust Score: {threatDetails.trustScore}
                                 </Typography>
                              </Grid>
                           </Grid>
                        )}
                     </AsyncContainer>
                  ) : (
                     <Typography>Select an IP to view threat details</Typography>
                  )}
               </Panel>
            </Grid>

            <Grid item sx={{textAlign: 'center'}}>
               <Button onClick={onClose}>Close</Button>
            </Grid>
         </Grid>
      </DialogComponent>
   );
};

export default ThreatIntelCacheDialog;
